import {Button, DropdownMenu, IconButton} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {type ReactNode, useCallback} from 'react';
import {type Selection} from 'react-aria-components';

import {useCriticalWorkflowTrigger} from '../../../util/datadog/criticalWorkflows/useCriticalWorkflowTrigger';
import {EMPTY_CONFIG} from '../../../util/runfeed';

interface WBTablePaginationProps {
  loading: boolean;
  currentPage: number;
  maxPage: number;
  currentPageSize?: number;
  totalRowCount: number;
  onClickPrevPage(): void;
  onClickNextPage(): void;
  setPageSize(newPageSize: number): void;
}

const PAGE_SIZES = [10, 20, 50, 100];

export function WBTablePagination({
  loading,
  currentPage,
  maxPage,
  onClickPrevPage,
  onClickNextPage,
  setPageSize,
  totalRowCount,
  currentPageSize = EMPTY_CONFIG.pageSize,
}: WBTablePaginationProps) {
  const {captureInitiatingEvent, observe: observePagination} =
    useCriticalWorkflowTrigger('run-selector-pagination', {
      windowDurationMs: 2_000,
    });

  function getDisplayedValue(): string {
    if (totalRowCount <= 0) {
      return '0';
    }
    const start = 1 + (currentPage - 1) * currentPageSize;
    const end = Math.min(totalRowCount, currentPage * currentPageSize);
    return `${start.toLocaleString()}-${end.toLocaleString()}`;
  }

  const isPrevDisabled = loading || currentPage === 1;
  const isNextDisabled = loading || currentPage === maxPage;

  const onSelectionChange = useCallback(
    (keys: Selection) => {
      if (keys === 'all') {
        return;
      }
      const [key] = [...keys];
      if (key == null) {
        return;
      }
      const nextPageSize = Number(key);
      observePagination({
        action: 'page_size',
        currentPage,
        previousPageSize: currentPageSize,
        nextPageSize,
        totalItemCount: totalRowCount,
      });
      setPageSize(nextPageSize);
    },
    [
      currentPage,
      currentPageSize,
      observePagination,
      setPageSize,
      totalRowCount,
    ]
  );

  return (
    <div className="flex justify-end">
      <div className="flex gap-6 items-center py-6 px-8">
        <DropdownMenu.Trigger>
          <Button
            variant="ghost"
            size="sm"
            endIcon="chevron-down"
            data-test="pageSize-popup"
            aria-label="Select page size"
            data-dd-action-name="Run selector pagination page size"
            isDisabled={loading}>
            {getDisplayedValue()}
          </Button>
          <DropdownMenu.Content
            // Pagination sits at the bottom of the viewport, so open the menu
            // upward to keep every page-size option on-screen and clickable.
            placement="top end"
            selectionMode="single"
            disallowEmptySelection
            selectedKeys={[currentPageSize]}
            onSelectionChange={onSelectionChange}>
            {PAGE_SIZES.map(size => (
              <DropdownMenu.Item key={size} id={size} data-test="pageSize-set">
                <div onPointerDownCapture={captureInitiatingEvent}>
                  <DropdownMenu.ItemLabel>{`${size}`}</DropdownMenu.ItemLabel>
                </div>
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Trigger>
        <span className="whitespace-nowrap">
          of {totalRowCount.toLocaleString()}
        </span>
        <div
          className="shrink-0"
          onKeyDownCapture={captureInitiatingEvent}
          onPointerDownCapture={captureInitiatingEvent}>
          <IconButton
            icon="chevron-back"
            aria-label="Go to previous page"
            data-dd-action-name="Run selector pagination"
            isDisabled={isPrevDisabled}
            onPress={() => {
              observePagination({
                action: 'previous_page',
                fromPage: currentPage,
                toPage: currentPage - 1,
                pageSize: currentPageSize,
                totalItemCount: totalRowCount,
              });
              onClickPrevPage();
            }}
            variant="ghost"
          />
          <IconButton
            icon="chevron-next"
            aria-label="Go to next page"
            data-dd-action-name="Run selector pagination"
            isDisabled={isNextDisabled}
            onPress={() => {
              observePagination({
                action: 'next_page',
                fromPage: currentPage,
                toPage: currentPage + 1,
                pageSize: currentPageSize,
                totalItemCount: totalRowCount,
              });
              onClickNextPage();
            }}
            variant="ghost"
          />
        </div>
      </div>
    </div>
  );
}

type WBTablePaginationContainerProps = {
  children: ReactNode;
  floating: boolean;
};

export function WBTablePaginationContainer({
  children,
  floating,
}: WBTablePaginationContainerProps) {
  if (!floating) {
    return <TailwindContents>{children}</TailwindContents>;
  }

  return (
    <TailwindContents>
      <div className="sticky bottom-0 height-0 z-[105]">
        <div className="bg-white shadow-[0_2px_6px_rgba(0,0,0,0.1)] right-16 bottom-8 absolute rounded-lg">
          {children}
        </div>
      </div>
    </TailwindContents>
  );
}
